/**
 * Renders the first-run wizard OVER the app until setup is recorded complete.
 *
 * An overlay, not a block: children always mount. Returning nothing while the
 * setup state loads would blank the screen on every workspace switch, since
 * this sits on the remount boundary — and the app underneath is what the
 * wizard's own reused panels need in order to work.
 *
 * Mounted UNDER `AppRuntimeProvider`: every surface the wizard reuses depends
 * on that runtime, so a gate placed above it could not render them. A failed
 * read shows no wizard at all — a backend that cannot answer must not lock the
 * person out of their app.
 */
import type { JSX, ReactNode } from "react";
import { useState } from "react";

import { useRpcQuery } from "../../runtime/hooks/useRpc";
import { LOCAL_WORKSPACE_ID, useWorkspaceStore } from "../../store/workspaceStore";
import { SetupFlow, type SetupState } from "./SetupFlow";
import { resolve, resumeStep } from "./setupState";

export function SetupGate({ children }: { readonly children: ReactNode }): JSX.Element {
  const [doneLocally, setDoneLocally] = useState(false);
  const workspaceId = useWorkspaceStore((s) => s.selectedWorkspaceId);
  const { data, isLoading, isError } = useRpcQuery<SetupState>(["setup", "get"], "setup.get");

  // Local workspace ONLY. The setup value lives in `app_settings`, which has
  // no `user_id` column, while source authorization is bound to the caller —
  // so one row cannot honestly represent per-user cloud onboarding, and
  // showing this on a shared space would let one person's answers speak for
  // everybody. Cloud spaces are configured by an admin through the same
  // Settings panels this wizard reuses.
  const isLocal = workspaceId === LOCAL_WORKSPACE_ID;
  const show = isLocal && !isLoading && !isError && !doneLocally && data?.completed !== true;

  // Resume where an interrupted run stopped. Restarting at Welcome replayed
  // every choice AND re-ran every install; the step was already persisted,
  // it was simply never read back.
  const picks = data?.capabilities;
  const hasSources = resolve(picks ?? []).sources.length > 0;
  const step = resumeStep(data?.current_step, hasSources);

  return (
    <>
      {children}
      {show && (
        <SetupFlow
          initialStep={step}
          initialEngine={data?.engine ?? null}
          initialFailed={data?.deferred ?? []}
          {...(picks !== undefined && picks.length > 0 ? { initialPicks: picks } : {})}
          onFinished={() => { setDoneLocally(true); }}
        />
      )}
    </>
  );
}
